import type { HudDisplayMode } from '../hud/HudOverlay';
import type { DisplayUnits } from '../hud/format';
import type { CameraMode } from '../render/cameras/CameraRig';
import type { RenderQuality } from '../render/RenderQuality';
import type { RadarVizLayer } from '../render/radarViz/RadarViz';

export const UI_STATE_STORAGE_KEY = 'similator:ui-state';

export type ControlMode = 'manual' | 'auto';
export type RadarLayerState = Record<RadarVizLayer, boolean>;

export interface UiState {
  cameraMode: CameraMode;
  renderQuality: RenderQuality;
  displayUnits: DisplayUnits;
  hudMode: HudDisplayMode;
  radarLayers: RadarLayerState;
  controlMode: ControlMode;
}

export type UiStateUpdate = Partial<Omit<UiState, 'displayUnits' | 'radarLayers'>> & {
  displayUnits?: Partial<DisplayUnits>;
  radarLayers?: Partial<RadarLayerState>;
};

export type UiStateStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export const DEFAULT_UI_STATE: Readonly<UiState> = Object.freeze({
  cameraMode: 'fpv',
  renderQuality: 'med',
  displayUnits: Object.freeze({ length: 'm', speed: 'mps' }),
  hudMode: 'full',
  radarLayers: Object.freeze({
    coverage: true,
    sweep: true,
    tracks: true,
    halo: true,
    ppi: true,
  }),
  controlMode: 'auto',
});

const cameraModes: readonly CameraMode[] = ['fpv', 'chase', 'orbit', 'radar-eye'];
const renderQualities: readonly RenderQuality[] = ['low', 'med', 'high'];
const hudModes: readonly HudDisplayMode[] = ['full', 'compact', 'cinematic'];
const controlModes: readonly ControlMode[] = ['manual', 'auto'];
const radarLayers: readonly RadarVizLayer[] = ['coverage', 'sweep', 'tracks', 'halo', 'ppi'];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const includes = <T extends string>(values: readonly T[], value: unknown): value is T =>
  typeof value === 'string' && values.includes(value as T);

const cloneState = (state: Readonly<UiState>): UiState => ({
  ...state,
  displayUnits: { ...state.displayUnits },
  radarLayers: { ...state.radarLayers },
});

const parseStoredState = (value: unknown): UiState => {
  if (!isRecord(value)) return cloneState(DEFAULT_UI_STATE);
  const units = isRecord(value.displayUnits) ? value.displayUnits : {};
  const layers = isRecord(value.radarLayers) ? value.radarLayers : {};
  const defaults = DEFAULT_UI_STATE;
  return {
    cameraMode: includes(cameraModes, value.cameraMode) ? value.cameraMode : defaults.cameraMode,
    renderQuality: includes(renderQualities, value.renderQuality)
      ? value.renderQuality
      : defaults.renderQuality,
    displayUnits: {
      length: includes(['m', 'ft'] as const, units.length)
        ? units.length
        : defaults.displayUnits.length,
      speed: includes(['mps', 'kmh'] as const, units.speed)
        ? units.speed
        : defaults.displayUnits.speed,
    },
    hudMode: includes(hudModes, value.hudMode) ? value.hudMode : defaults.hudMode,
    radarLayers: Object.fromEntries(
      radarLayers.map((layer) => [
        layer,
        typeof layers[layer] === 'boolean' ? layers[layer] : defaults.radarLayers[layer],
      ]),
    ) as RadarLayerState,
    controlMode: includes(controlModes, value.controlMode)
      ? value.controlMode
      : defaults.controlMode,
  };
};

/** Small persisted preference store with validation at the storage boundary. */
export class UiStateStore {
  private current: UiState;

  constructor(private readonly storage: UiStateStorage) {
    this.current = this.restore();
  }

  get state(): UiState {
    return cloneState(this.current);
  }

  update(update: UiStateUpdate): UiState {
    this.current = parseStoredState({
      ...this.current,
      ...update,
      displayUnits: { ...this.current.displayUnits, ...update.displayUnits },
      radarLayers: { ...this.current.radarLayers, ...update.radarLayers },
    });
    this.persist();
    return this.state;
  }

  private restore(): UiState {
    const stored = this.storage.getItem(UI_STATE_STORAGE_KEY);
    if (stored === null) return cloneState(DEFAULT_UI_STATE);
    try {
      return parseStoredState(JSON.parse(stored) as unknown);
    } catch {
      return cloneState(DEFAULT_UI_STATE);
    }
  }

  private persist(): void {
    this.storage.setItem(UI_STATE_STORAGE_KEY, JSON.stringify(this.current));
  }
}
